import React, { useState, useEffect } from 'react';
import { vlanProfileAPI, planAPI, branchAPI, templateAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import { toast } from 'react-toastify';
import './VlanProfiles.css';

const DEFAULT_FORM = {
    branchId: '',
    vlanId: '',
    name: '',
    planId: '',
    templateId: '',
    bandwidth: { uploadSpeed: '', downloadSpeed: '' },
    sessionTimeout: '',
    idleTimeout: '',
    dataQuota: '',
    firewallPolicy: '',
    isActive: true,
    networkConfig: { gateway: '', subnet: '', poolRange: '', dns: '' },
};

const VlanProfiles = () => {
    const { selectedBranch } = useBranchStore();
    const [profiles, setProfiles] = useState([]);
    const [plans, setPlans] = useState([]);
    const [branches, setBranches] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [viewing, setViewing] = useState(null);
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchAll();
    }, [selectedBranch]);

    const fetchAll = async () => {
        try {
            setLoading(true);
            const branchId = selectedBranch ? selectedBranch._id : undefined;
            const [vpRes, planRes, branchRes, tplRes] = await Promise.all([
                vlanProfileAPI.getAll(branchId ? { branchId } : undefined),
                planAPI.getAll(branchId),
                branchAPI.getAll(),
                templateAPI.getAll(branchId ? { branchId, limit: 200 } : { limit: 200 }),
            ]);
            setProfiles(vpRes.data.data || []);
            setPlans(planRes.data.data.plans || []);
            setBranches(branchRes.data.data.branches || []);
            setTemplates(tplRes.data.data || tplRes.data || []);
        } catch (error) {
            toast.error('Failed to fetch VLAN profiles');
        } finally {
            setLoading(false);
        }
    };

    const handleNew = () => {
        setEditing(null);
        setFormData({ ...DEFAULT_FORM, branchId: selectedBranch?._id || '' });
        setShowModal(true);
    };

    const handleEdit = (vp) => {
        setEditing(vp);
        setFormData({
            branchId: vp.branchId?._id || vp.branchId || '',
            vlanId: vp.vlanId,
            name: vp.name,
            planId: vp.planId?._id || vp.planId || '',
            templateId: vp.templateId?._id || vp.templateId || '',
            bandwidth: { uploadSpeed: vp.bandwidth?.uploadSpeed || '', downloadSpeed: vp.bandwidth?.downloadSpeed || '' },
            sessionTimeout: vp.sessionTimeout || '',
            idleTimeout: vp.idleTimeout || '',
            dataQuota: vp.dataQuota || '',
            firewallPolicy: vp.firewallPolicy || '',
            isActive: vp.isActive !== false,
            networkConfig: {
                gateway:   vp.networkConfig?.gateway   || '',
                subnet:    vp.networkConfig?.subnet     || '',
                poolRange: vp.networkConfig?.poolRange  || '',
                dns:       vp.networkConfig?.dns        || '',
            },
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.branchId) return toast.warning('Branch is required');
        if (!formData.vlanId) return toast.warning('VLAN ID is required');
        if (!formData.name.trim()) return toast.warning('Name is required');
        try {
            // Strip empty strings from optional ref and numeric fields so Mongoose
            // doesn't throw a CastError trying to cast '' to ObjectId or Number.
            const payload = {
                branchId: formData.branchId,
                vlanId: formData.vlanId,
                name: formData.name,
                isActive: formData.isActive,
                ...(formData.planId     ? { planId:     formData.planId }     : {}),
                ...(formData.templateId ? { templateId: formData.templateId } : {}),
                ...(formData.firewallPolicy.trim() ? { firewallPolicy: formData.firewallPolicy } : {}),
                ...(formData.sessionTimeout !== '' ? { sessionTimeout: Number(formData.sessionTimeout) } : {}),
                ...(formData.idleTimeout    !== '' ? { idleTimeout:    Number(formData.idleTimeout) }    : {}),
                ...(formData.dataQuota      !== '' ? { dataQuota:      Number(formData.dataQuota) }      : {}),
                bandwidth: {
                    ...(formData.bandwidth.downloadSpeed !== '' ? { downloadSpeed: Number(formData.bandwidth.downloadSpeed) } : {}),
                    ...(formData.bandwidth.uploadSpeed   !== '' ? { uploadSpeed:   Number(formData.bandwidth.uploadSpeed) }   : {}),
                },
                networkConfig: {
                    ...(formData.networkConfig.gateway   ? { gateway:   formData.networkConfig.gateway }   : {}),
                    ...(formData.networkConfig.subnet    ? { subnet:    formData.networkConfig.subnet }    : {}),
                    ...(formData.networkConfig.poolRange ? { poolRange: formData.networkConfig.poolRange } : {}),
                    ...(formData.networkConfig.dns       ? { dns:       formData.networkConfig.dns }       : {}),
                },
            };
            if (editing) {
                await vlanProfileAPI.update(editing._id, payload);
                toast.success('VLAN profile updated');
            } else {
                await vlanProfileAPI.create(payload);
                toast.success('VLAN profile created');
            }
            setShowModal(false);
            fetchAll();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleView = (vp) => {
        setViewing(vp);
    };

    const filteredProfiles = profiles.filter((vp) => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return true;
        return [String(vp.vlanId), vp.name, vp.branchId?.name, vp.planId?.name]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term));
    });

    const handleDelete = async (vp) => {
        if (!window.confirm(`Delete VLAN ${vp.vlanId} profile "${vp.name}"?`)) return;
        try {
            await vlanProfileAPI.delete(vp._id);
            toast.success('VLAN profile deleted');
            fetchAll();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-network-wired"></i>
                            VLAN Profiles
                        </h1>
                        <p className="page-subtitle-modern">Map VLAN IDs to plans with independent bandwidth, timeout, and quota — auto-applied when the switch/AP tags traffic by VLAN</p>
                    </div>
                    <div className="header-actions">
                        <button className="btn-primary-modern" onClick={handleNew}>
                            <i className="fas fa-plus-circle"></i>
                            Add VLAN Profile
                        </button>
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern"><div className="spinner-modern"></div><p>Loading VLAN profiles...</p></div>
            ) : profiles.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-network-wired"></i></div>
                    <h3>No VLAN Profiles Yet</h3>
                    <p>e.g. VLAN 10 → Staff Internet, VLAN 20 → Guest Internet, VLAN 30 → VIP Internet</p>
                </div>
            ) : (
                <div className="table-wrapper-modern">
                    <div className="table-search-bar">
                        <div className="search-box-modern">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search by VLAN ID, name, branch, or plan..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="table-container">
                    <table className="table-modern">
                        <thead>
                            <tr>
                                <th>VLAN ID</th>
                                <th>Name</th>
                                <th>Branch</th>
                                <th>Plan</th>
                                <th>Bandwidth</th>
                                <th>Session Timeout</th>
                                <th>Data Quota</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredProfiles.length === 0 ? (
                                <tr><td colSpan={9} className="table-empty-row">No VLAN profile matches "{searchTerm}"</td></tr>
                            ) : filteredProfiles.map(vp => (
                                <tr key={vp._id}>
                                    <td><span className="code-display">{vp.vlanId}</span></td>
                                    <td>{vp.name}</td>
                                    <td>{vp.branchId?.name || '—'}</td>
                                    <td>{vp.planId?.name || '—'}</td>
                                    <td>{vp.bandwidth?.downloadSpeed || '—'}/{vp.bandwidth?.uploadSpeed || '—'} Kbps</td>
                                    <td>{vp.sessionTimeout ? `${vp.sessionTimeout}s` : '—'}</td>
                                    <td>{vp.dataQuota ? `${vp.dataQuota} MB` : 'Unlimited'}</td>
                                    <td>
                                        <span className={`status-badge ${vp.isActive ? 'active' : 'inactive'}`}>
                                            <span className="badge-dot"></span>{vp.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td>
                                        <div className="table-row-actions">
                                            <button className="action-btn-icon view" onClick={() => handleView(vp)} title="View"><i className="fas fa-eye"></i></button>
                                            <button className="action-btn-icon edit" onClick={() => handleEdit(vp)} title="Edit"><i className="fas fa-edit"></i></button>
                                            <button className="action-btn-icon delete" onClick={() => handleDelete(vp)} title="Delete"><i className="fas fa-trash"></i></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}

            {viewing && (
                <div className="modal-overlay-modern" onClick={() => setViewing(null)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>VLAN {viewing.vlanId} — {viewing.name}</h2>
                            <button className="modal-close" onClick={() => setViewing(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <div className="status-badges">
                                <span className={`status-badge ${viewing.isActive ? 'active' : 'inactive'}`}>
                                    <span className="badge-dot"></span>{viewing.isActive ? 'Active' : 'Inactive'}
                                </span>
                                {viewing.planId && <span className="status-badge inactive">{viewing.planId.name}</span>}
                            </div>
                            <div className="info-grid two-col">
                                <div className="info-item">
                                    <i className="fas fa-code-branch"></i>
                                    <div><span className="info-label">Branch</span><span className="info-value">{viewing.branchId?.name || '—'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-file-alt"></i>
                                    <div><span className="info-label">Portal Template</span><span className="info-value">{viewing.templateId?.name || 'Branch default'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-tachometer-alt"></i>
                                    <div><span className="info-label">Bandwidth</span><span className="info-value">{viewing.bandwidth?.downloadSpeed || '—'}/{viewing.bandwidth?.uploadSpeed || '—'} Kbps</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-hourglass-half"></i>
                                    <div><span className="info-label">Session Timeout</span><span className="info-value">{viewing.sessionTimeout ? `${viewing.sessionTimeout}s` : '—'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-clock"></i>
                                    <div><span className="info-label">Idle Timeout</span><span className="info-value">{viewing.idleTimeout ? `${viewing.idleTimeout}s` : '—'}</span></div>
                                </div>
                                <div className="info-item">
                                    <i className="fas fa-database"></i>
                                    <div><span className="info-label">Data Quota</span><span className="info-value">{viewing.dataQuota ? `${viewing.dataQuota} MB` : 'Unlimited'}</span></div>
                                </div>
                                {viewing.firewallPolicy && (
                                    <div className="info-item">
                                        <i className="fas fa-shield-alt"></i>
                                        <div><span className="info-label">Firewall Policy</span><span className="info-value">{viewing.firewallPolicy}</span></div>
                                    </div>
                                )}
                                {viewing.networkConfig?.gateway && (
                                    <div className="info-item" style={{ gridColumn: '1 / -1' }}>
                                        <i className="fas fa-network-wired" style={{ color: '#6366f1' }}></i>
                                        <div>
                                            <span className="info-label">IP Config</span>
                                            <span className="info-value" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                                GW: {viewing.networkConfig.gateway}
                                                {viewing.networkConfig.subnet    && ` · Subnet: ${viewing.networkConfig.subnet}`}
                                                {viewing.networkConfig.poolRange && ` · Pool: ${viewing.networkConfig.poolRange}`}
                                                {viewing.networkConfig.dns       && ` · DNS: ${viewing.networkConfig.dns}`}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setViewing(null)}>Close</button>
                            <button className="btn-primary-modern" onClick={() => { setViewing(null); handleEdit(viewing); }}><i className="fas fa-edit"></i> Edit</button>
                        </div>
                    </div>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay-modern" onClick={() => setShowModal(false)}>
                    <div className="modal-modern" onClick={e => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <h2>{editing ? 'Edit VLAN Profile' : 'Add VLAN Profile'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body-modern">
                                <div className="form-grid">
                                    <div className="form-group-modern">
                                        <label>Branch <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <select value={formData.branchId} onChange={e => setFormData({ ...formData, branchId: e.target.value })} required>
                                            <option value="">Select Branch</option>
                                            {branches.map(b => <option key={b._id} value={b._id}>{b.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern">
                                        <label>VLAN ID <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="number" min="1" max="4094" value={formData.vlanId} onChange={e => setFormData({ ...formData, vlanId: e.target.value })} placeholder="e.g. 20" required />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Name <span style={{ color: 'var(--danger-color)' }}>*</span></label>
                                        <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="e.g. Guest Internet" required />
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label>Portal Template <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 400 }}>(override branch default for this VLAN)</span></label>
                                        <select value={formData.templateId} onChange={e => setFormData({ ...formData, templateId: e.target.value })}>
                                            <option value="">— Use branch default —</option>
                                            {templates.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group-modern full-width">
                                        <label className="checkbox-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />
                                            <span>Active</span>
                                        </label>
                                    </div>
                                </div>

                                {/* ── MikroTik IP Config ── */}
                                <div style={{ marginTop: '1rem', borderRadius: '10px', border: '1px solid #c7d2fe', overflow: 'hidden' }}>
                                    <div style={{ background: '#eef2ff', padding: '0.65rem 1rem', fontWeight: '700', fontSize: '0.82rem', color: '#3730a3', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <i className="fas fa-network-wired"></i>
                                        MikroTik IP Configuration
                                        <span style={{ fontWeight: '400', color: '#6b7280', fontSize: '0.75rem' }}> — optional, used when Push Config is applied</span>
                                    </div>
                                    <div style={{ padding: '1rem', background: '#fff' }}>
                                        <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.85rem', lineHeight: '1.5' }}>
                                            If left blank, Push Config auto-generates <code style={{ background: '#f3f4f6', borderRadius: '3px', padding: '1px 4px' }}>192.168.{'{vlanId}'}.0/24</code> for this VLAN.
                                            Fill these to use your own subnet.
                                        </div>
                                        <div className="form-grid">
                                            <div className="form-group-modern">
                                                <label><i className="fas fa-gateway" style={{ marginRight: '0.35rem', color: '#6366f1' }}></i>Gateway IP</label>
                                                <input type="text" value={formData.networkConfig.gateway}
                                                    onChange={e => setFormData({ ...formData, networkConfig: { ...formData.networkConfig, gateway: e.target.value } })}
                                                    placeholder="e.g. 192.168.10.1" />
                                            </div>
                                            <div className="form-group-modern">
                                                <label><i className="fas fa-th" style={{ marginRight: '0.35rem', color: '#0ea5e9' }}></i>Subnet (CIDR)</label>
                                                <input type="text" value={formData.networkConfig.subnet}
                                                    onChange={e => setFormData({ ...formData, networkConfig: { ...formData.networkConfig, subnet: e.target.value } })}
                                                    placeholder="e.g. 192.168.10.0/24" />
                                            </div>
                                            <div className="form-group-modern">
                                                <label><i className="fas fa-random" style={{ marginRight: '0.35rem', color: '#f59e0b' }}></i>DHCP Pool Range</label>
                                                <input type="text" value={formData.networkConfig.poolRange}
                                                    onChange={e => setFormData({ ...formData, networkConfig: { ...formData.networkConfig, poolRange: e.target.value } })}
                                                    placeholder="e.g. 192.168.10.10-192.168.10.250" />
                                            </div>
                                            <div className="form-group-modern">
                                                <label><i className="fas fa-search" style={{ marginRight: '0.35rem', color: '#10b981' }}></i>DNS Servers</label>
                                                <input type="text" value={formData.networkConfig.dns}
                                                    onChange={e => setFormData({ ...formData, networkConfig: { ...formData.networkConfig, dns: e.target.value } })}
                                                    placeholder="e.g. 8.8.8.8,8.8.4.4" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern"><i className="fas fa-check"></i> {editing ? 'Update' : 'Create'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default VlanProfiles;
